import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bell,
  Bot,
  BriefcaseBusiness,
  Building2,
  ClipboardCheck,
  FileStack,
  FolderKanban,
  HardDrive,
  LayoutDashboard,
  Map,
  MessageSquare,
  Palette,
  ShieldCheck,
  Sparkles,
  Store,
  UserCheck,
  Users,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { IntelligenceWidgetGrid } from "../../components/dashboard/IntelligenceWidgetGrid";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { getAdminClients, getAdminJourneyDashboard } from "../../lib/api";
import { buildDemoBusinessCenterIntelligence } from "../../lib/dashboardIntelligence";
import {
  createUniversalOperationalFoundationState,
  registerOperationalRecords,
  type UniversalOperationalFoundationState,
} from "../../lib/universalOperationalEngines";
import { getRegistryHealth, getRegistryStatsMap } from "../../lib/operationalRegistryDiagnostics";
import {
  getRecentActivityByWorkspace,
  getTicklersDueToday,
  loadOperationalState,
} from "../../lib/universalOperationalPersistence";
import type { ClientRecord, JourneySummary } from "../../lib/types";
import { buildSeedRegistryRecords } from "./universalOperationalRegistrySeeds";

const workspaceCards = [
  {
    title: "Truevance Connect",
    subtitle: "Business Consulting & Transformation",
    description: "Sales, assessments, recommendations, proposals, delivery, and follow-up in one consulting workspace.",
    to: "/admin/truevance-business-center",
    status: "Active",
    useCase: "Review assessment recommendations before they move into client-facing strategy.",
    category: "Industry Workspaces",
    icon: BriefcaseBusiness,
    accent: "text-[#38BDF8]",
  },
  {
    title: "Kreatethedate",
    subtitle: "Event Experience Platform",
    description: "Owned event inquiry, RSVP, memory, archive, song, highlight reel, and referral form operations.",
    to: "/admin/forms-directory",
    status: "Active",
    useCase: "Track every inquiry from request intake to service delivery.",
    category: "Industry Workspaces",
    icon: Sparkles,
    accent: "text-[#FF6A2B]",
  },
  {
    title: "Memory Media Vault",
    subtitle: "Media Preservation Platform",
    description: "Media collection, review, approval, delivery, archive, and preservation workflows.",
    to: "/admin/memory-hub/vault",
    status: "Active",
    useCase: "Review files before they become client-visible.",
    category: "Client Experience",
    icon: HardDrive,
    accent: "text-[#A78BFA]",
  },
  {
    title: "CollabRecover AR Solutions",
    subtitle: "Accounts Receivable Recovery Platform",
    description: "Aging, promise-to-pay, ticklers, letters, statements, escalation, activity, and reporting.",
    to: "/admin/ar-recovery",
    status: "Active",
    useCase: "See follow-ups, broken promises, and accounts requiring collector action.",
    category: "Industry Workspaces",
    icon: AlertTriangle,
    accent: "text-[#FB923C]",
  },
] as const;

const sharedModuleCards = [
  {
    title: "Executive Dashboard",
    description: "Shared executive visibility across business health, delivery, revenue, and risk.",
    to: "/admin/executive-dashboard",
    icon: LayoutDashboard,
    category: "Core Operations",
    status: "Ready",
    useCase: "Review platform health before leadership check-ins.",
  },
  {
    title: "Leads",
    description: "Business development intake and follow-up pipeline.",
    to: "/admin/leads",
    icon: Workflow,
    category: "Core Operations",
    status: "Ready",
    useCase: "Track every lead from inquiry to qualified next step.",
  },
  {
    title: "Client Center",
    description: "Shared client records, contacts, company context, and portal preparation.",
    to: "/admin/truevance-client-center",
    icon: Users,
    category: "Core Operations",
    status: "Ready",
    useCase: "See which clients are waiting on your team.",
  },
  {
    title: "Project Center",
    description: "Shared project delivery, milestones, ownership, and delivery posture.",
    to: "/admin/truevance-project-center",
    icon: FolderKanban,
    category: "Core Operations",
    status: "Ready",
    useCase: "Find projects that need review before the next client update.",
  },
  {
    title: "Document Center",
    description: "Shared client, project, proposal, agreement, and operational documents.",
    to: "/admin/truevance-document-center",
    icon: FileStack,
    category: "Client Experience",
    status: "Ready",
    useCase: "Confirm client documents are complete before onboarding advances.",
  },
  {
    title: "Task Center",
    description: "Shared tasks, follow-ups, assignments, and internal operational work.",
    to: "/admin/truevance-tasks",
    icon: ClipboardCheck,
    category: "Core Operations",
    status: "Ready",
    useCase: "Prioritize open work that is blocking client progress.",
  },
  {
    title: "Agreement Records",
    description: "Shared service agreements, signature history, and approval state.",
    to: "/admin/agreement-records",
    icon: FileStack,
    category: "Client Experience",
    status: "Ready",
    useCase: "Confirm signed agreements before delivery work starts.",
  },
  {
    title: "Payment Records",
    description: "Shared invoices, deposits, balances, and payment history.",
    to: "/admin/payment-records",
    icon: BarChart3,
    category: "Core Operations",
    status: "Ready",
    useCase: "Identify accounts where payment status may slow delivery.",
  },
  {
    title: "Client Onboarding Center",
    description: "Shared onboarding, portal setup, branding, workspace assignment, and launch readiness.",
    to: "/admin/client-onboarding-center",
    icon: UserCheck,
    category: "Client Experience",
    status: "Ready",
    useCase: "Move new clients from accepted proposal to active workspace.",
  },
  {
    title: "Universal Client Portal",
    description: "Client-facing access layer for tasks, documents, activity, billing, messages, and workspace context.",
    to: "/client/portal",
    icon: MessageSquare,
    category: "Client Experience",
    status: "Ready",
    useCase: "Preview how clients experience work, approvals, and updates.",
  },
  {
    title: "User Management",
    description: "Shared user and company access management.",
    to: "/admin/universal-user-management",
    icon: Users,
    category: "Platform Administration",
    status: "Ready",
    useCase: "Keep client, team, and company access aligned.",
  },
  {
    title: "Roles & Permissions",
    description: "Role definitions, access levels, permission mapping, and governance rules.",
    to: "/admin/truevance-roles-center",
    icon: ShieldCheck,
    category: "Platform Administration",
    status: "Ready",
    useCase: "Confirm the right users can approve sensitive work.",
  },
  {
    title: "Workspace Provisioning",
    description: "Workspace creation, entitlement assignment, lifecycle controls, and setup governance.",
    to: "/admin/workspace-provisioning-center",
    icon: Building2,
    category: "Platform Administration",
    status: "Ready",
    useCase: "Provision a new workspace without losing ownership context.",
  },
  {
    title: "White Label Branding",
    description: "Brand profiles, labels, portal presentation, and client-facing workspace identity.",
    to: "/admin/white-label-branding-center",
    icon: Palette,
    category: "Platform Administration",
    status: "Ready",
    useCase: "Confirm client portals use the correct brand profile.",
  },
  {
    title: "Automation Engine",
    description: "Workflow triggers, conditions, actions, approvals, and operational automation.",
    to: "/admin/automation-engine",
    icon: Workflow,
    category: "Intelligence & Automation",
    status: "Ready",
    useCase: "Resume paused automations after a human approval check.",
  },
] as const;

const administrationCards = [
  {
    title: "Audit & Activity Center",
    description: "Logs, events, history, and operational activity across the platform.",
    to: "/admin/activity-log",
    icon: Activity,
    category: "Platform Administration",
    status: "Ready",
    useCase: "Review recent platform activity before escalation.",
  },
  {
    title: "Notification Center",
    description: "Future notification hub for alerts, operational events, and routing.",
    icon: Bell,
    category: "Intelligence & Automation",
    status: "Coming Soon",
    useCase: "Centralize future alerts without creating a broken route.",
  },
  {
    title: "Subscription & Entitlement Center",
    description: "Billing bridge, plan posture, capacity, and entitlement governance.",
    to: "/admin/payment-records",
    icon: BarChart3,
    category: "Platform Administration",
    status: "Ready",
    useCase: "Review plan or capacity pressure before enabling more workspaces.",
  },
  {
    title: "Product Registry",
    description: "Future catalog of platform products, workspace offerings, and installable capabilities.",
    icon: Building2,
    category: "Intelligence & Automation",
    status: "Coming Soon",
    useCase: "Prepare product ownership without linking to an unfinished page.",
  },
  {
    title: "Governance Center",
    description: "Future rules, policies, compliance controls, and administrative review.",
    icon: ShieldCheck,
    category: "Platform Administration",
    status: "Coming Soon",
    useCase: "Prepare governance controls without changing access behavior.",
  },
] as const;

const futureServiceCards = [
  {
    title: "Marketplace",
    description: "Solutions, bundles, connectors, industry packs, and commercial upgrades.",
    icon: Store,
    category: "Intelligence & Automation",
    status: "Coming Soon",
    useCase: "Recommend the next installable solution without creating a broken link.",
  },
  {
    title: "AI Center",
    description: "Operational intelligence, recommendations, summaries, and human-reviewed guidance.",
    icon: Bot,
    category: "Intelligence & Automation",
    status: "Coming Soon",
    useCase: "Review AI-style recommendations before client-facing action.",
  },
  {
    title: "Template Library",
    description: "Workspace, portal, document, and workflow templates.",
    icon: FileStack,
    category: "Intelligence & Automation",
    status: "Coming Soon",
    useCase: "Standardize repeatable service workflows.",
  },
  {
    title: "Workflow Builder",
    description: "Design-time workflow authoring for the automation engine.",
    icon: Workflow,
    category: "Intelligence & Automation",
    status: "Coming Soon",
    useCase: "Design future workflows before activating automation.",
  },
] as const;

const activeStatuses = new Set([
  "discovery scheduled",
  "discovery completed",
  "proposal sent",
  "proposal viewed",
  "proposal accepted",
  "agreement sent",
  "agreement viewed",
  "agreement signed",
  "payment pending",
  "partial payment received",
  "awaiting final payment",
  "welcome sent",
  "onboarding sent",
  "onboarding complete",
  "kickoff call scheduled",
  "kickoff call confirmed",
  "kickoff call completed",
  "project active",
  "build in progress",
  "internal review",
  "client review",
  "revision requested",
  "final approval",
  "project complete",
  "delivered",
]);

const priorities = [
  {
    level: "High",
    module: "Client Onboarding",
    title: "Client onboarding waiting on missing documents",
    why: "The workspace is ready, but delivery should not move forward until required files are complete.",
    action: "Review onboarding checklist",
    to: "/admin/client-onboarding-center",
    tone: "border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#FDE68A]",
  },
  {
    level: "High",
    module: "AR Recovery",
    title: "Promise-to-pay follow-up due today",
    why: "Collector action is needed before the account becomes a broken-promise risk.",
    action: "Open smart tickler",
    to: "/admin/ar-recovery/smart-tickler",
    tone: "border-[#FB923C]/50 bg-[#FB923C]/10 text-[#FDBA74]",
  },
  {
    level: "Medium",
    module: "Memory Media Vault",
    title: "Vault uploads awaiting review",
    why: "Files should be approved, hidden, or routed back before they appear in client-facing delivery.",
    action: "Review vault",
    to: "/admin/memory-hub/vault",
    tone: "border-[#A78BFA]/50 bg-[#A78BFA]/10 text-[#DDD6FE]",
  },
  {
    level: "Medium",
    module: "Truevance Connect",
    title: "Assessment recommendation pending",
    why: "A human should review the recommendation before it becomes client-facing guidance.",
    action: "Open Truevance hub",
    to: "/admin/truevance-business-center",
    tone: "border-[#38BDF8]/50 bg-[#38BDF8]/10 text-[#BAE6FD]",
  },
  {
    level: "Approval",
    module: "Automation Engine",
    title: "Workflow automation paused for approval",
    why: "The automation can continue after the owner confirms the next client-facing step.",
    action: "Review automation",
    to: "/admin/automation-engine",
    tone: "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#BBF7D0]",
  },
] as const;

const journeyStages = [
  {
    label: "Lead",
    status: "New intake",
    action: "Qualify inquiry",
    to: "/admin/leads",
  },
  {
    label: "Discovery",
    status: "Calls and needs",
    action: "Review notes",
    to: "/admin/journey-dashboard",
  },
  {
    label: "Proposal",
    status: "Offer pending",
    action: "Check proposal status",
    to: "/admin/proposal-dashboard",
  },
  {
    label: "Onboarding",
    status: "Setup required",
    action: "Collect missing docs",
    to: "/admin/client-onboarding-center",
  },
  {
    label: "Active Work",
    status: "Delivery moving",
    action: "Review projects",
    to: "/admin/truevance-project-center",
  },
  {
    label: "Review/Approval",
    status: "Client decision",
    action: "Check open tasks",
    to: "/admin/truevance-tasks",
  },
  {
    label: "Delivery",
    status: "Assets and files",
    action: "Open documents",
    to: "/admin/truevance-document-center",
  },
  {
    label: "Retention",
    status: "Follow-up",
    action: "Review activity",
    to: "/admin/activity-log",
  },
] as const;

const recentActivity = [
  { time: "8:45 AM", module: "Client Portal", action: "Portal invite sent for onboarding review", status: "Sent" },
  { time: "9:20 AM", module: "Memory Media Vault", action: "Vault upload awaiting admin review", status: "Needs review" },
  { time: "10:05 AM", module: "AR Recovery", action: "Promise-to-pay follow-up due for collector queue", status: "Due today" },
  { time: "11:15 AM", module: "Automation Engine", action: "Agreement workflow step completed", status: "Complete" },
  { time: "12:30 PM", module: "Truevance Connect", action: "Assessment recommendation generated", status: "Human review" },
  { time: "1:10 PM", module: "Documents", action: "Client document uploaded to shared record", status: "Received" },
] as const;

export function BusinessCenterOSHomePage() {
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [journeys, setJourneys] = useState<JourneySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [operationalState, setOperationalState] = useState<UniversalOperationalFoundationState>(() => createUniversalOperationalFoundationState());
  const [operationalPersistenceStatus, setOperationalPersistenceStatus] = useState("Operational records use local fallback until backend sync completes.");

  const intelligence = useMemo(
    () =>
      buildDemoBusinessCenterIntelligence(
        {
          companyId: null,
          workspaceId: null,
          clientId: null,
          productKey: null,
        },
        "admin",
      ),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    document.title = "Business Center OS Command Center | Unified Business Platform";
    setLoading(true);
    setError("");

    Promise.all([getAdminClients(), getAdminJourneyDashboard()])
      .then(([clientData, journeyData]) => {
        if (cancelled) return;
        setClients(clientData);
        setJourneys(journeyData.journeys || []);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load Business Center OS home.");
          setClients([]);
          setJourneys([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    void loadOperationalState().then(async (result) => {
      if (cancelled) return;
      const seededRecords = await buildSeedRegistryRecords();
      if (cancelled) return;
      setOperationalState(registerOperationalRecords(result.state, seededRecords));
      setOperationalPersistenceStatus(
        result.backendAvailable
          ? "Operational records are synced through the backend state store with local fallback."
          : "Backend sync is unavailable; operational records are saved locally in this browser.",
      );
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const activeWorkflows = useMemo(
    () => journeys.filter((journey) => activeStatuses.has((journey.currentStatus || "").toLowerCase())).length,
    [journeys],
  );

  const statusStrip = [
    { label: "Active clients", value: loading ? "..." : clients.length || intelligence.health.clientsRequiringFollowUp + 8 },
    { label: "Open workflows", value: loading ? "..." : activeWorkflows || intelligence.projectIntelligence.delayRisks + 9 },
    { label: "Items needing review", value: priorities.length },
    { label: "Ticklers due today", value: getTicklersDueToday(operationalState).length },
  ];

  const overdueTicklers = operationalState.ticklers.filter((tickler) => tickler.status !== "Completed" && (tickler.status === "Overdue" || tickler.dueDate < new Date().toISOString().slice(0, 10)));
  const recentOperationalActivity = getRecentActivityByWorkspace(operationalState, "ar-recovery-os", 5);
  const activeBusinessNotes = operationalState.businessNotes.filter((note) => note.status !== "archived");
  const recentBusinessNotes = activeBusinessNotes
    .filter((note) => new Date(note.updatedAt || note.timestamp).getTime() >= Date.now() - 7 * 86400000)
    .slice(0, 5);
  const arLettersPrepared = operationalState.collectionLetterHistory.filter((letter) => letter.title === "Letter prepared").length;
  const arLettersSent = operationalState.collectionLetterHistory.filter((letter) => letter.deliveryStatus === "Sent/Logged").length;
  const arRecoveryNotesAdded = operationalState.arRecoveryNotes.length;
  const activeRegistryRecords = operationalState.operationalRecords.filter((record) => !record.archivedAt);
  const registryStats = getRegistryStatsMap(operationalState, activeRegistryRecords);
  const registryHealth = getRegistryHealth(activeRegistryRecords, registryStats);
  const hasOperationalRecords =
    operationalState.activityEvents.length ||
    operationalState.businessNotes.length ||
    operationalState.ticklers.length ||
    operationalState.auditTrail.length ||
    operationalState.arRecoveryNotes.length ||
    operationalState.collectionLetterHistory.length ||
    operationalState.operationalRecords.length;
  const operationalSummaryCards = [
    { label: "Ticklers Due Today", value: getTicklersDueToday(operationalState).length, note: "Universal reminders due today across workspaces." },
    { label: "Overdue Ticklers", value: overdueTicklers.length, note: "Open reminders past their due date or marked overdue." },
    { label: "Recent Activity", value: recentOperationalActivity.length, note: "Recent AR Recovery activity events from the universal engine." },
    { label: "Business Notes This Week", value: recentBusinessNotes.length, note: "Universal business notes updated in the last 7 days." },
    { label: "AR Letters Prepared/Sent", value: `${arLettersPrepared}/${arLettersSent}`, note: "Prepared and sent/logged collection letter actions." },
    { label: "AR Recovery Notes", value: arRecoveryNotesAdded, note: "Recovery notes added through account workspaces." },
    { label: "Registry Health", value: `${registryHealth.healthyRecords}/${registryHealth.totalRecords}`, note: "Healthy registered records across the operational registry." },
    { label: "Registry Gaps", value: registryHealth.recordsMissingOwners + registryHealth.recordsMissingNotes + registryHealth.recordsMissingTicklers, note: "Missing owner, note, or tickler diagnostics from the registry inspector." },
  ];

  const journeyCounts = useMemo(() => {
    const base = journeys.length || 16;
    return journeyStages.map((stage, index) => ({
      ...stage,
      count: Math.max(1, base - index * 2),
    }));
  }, [journeys.length]);

  const moduleGroups = useMemo(() => {
    const allModules = [...workspaceCards, ...sharedModuleCards, ...administrationCards, ...futureServiceCards];
    return [
      {
        title: "Core Operations",
        description: "The daily operating path for leads, clients, projects, workflows, payments, and reports.",
        modules: allModules.filter((module) => module.category === "Core Operations"),
      },
      {
        title: "Client Experience",
        description: "Portal, document, message, vault, agreement, and delivery surfaces that shape client visibility.",
        modules: allModules.filter((module) => module.category === "Client Experience"),
      },
      {
        title: "Intelligence & Automation",
        description: "Automation, dashboards, marketplace concepts, and AI-style guidance with human review.",
        modules: allModules.filter((module) => module.category === "Intelligence & Automation"),
      },
      {
        title: "Industry Workspaces",
        description: "Specialized operating systems that plug into the same platform layer.",
        modules: allModules.filter((module) => module.category === "Industry Workspaces"),
      },
      {
        title: "Platform Administration",
        description: "Access, branding, governance, provisioning, audit, and entitlement controls.",
        modules: allModules.filter((module) => module.category === "Platform Administration"),
      },
    ];
  }, []);

  return (
    <BusinessCenterOSAdminShell
      title="Business Center OS Command Center"
      eyebrow="Operating Layer"
      description="The operating layer for leads, clients, workflows, portals, automations, reports, and industry workspaces. Keep approvals, client movement, delivery risk, and workspace activity visible in one place."
      statusItems={statusStrip}
      actions={
        <>
          <a
            href="#today-priorities"
            className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-cyan-300 px-5 text-sm font-black text-slate-950 transition hover:-translate-y-0.5 hover:bg-cyan-200"
          >
            Review Today's Priorities
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>
          <a
            href="#workspace-map"
            className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/8 px-5 text-sm font-black text-white transition hover:-translate-y-0.5 hover:bg-white/12"
          >
            Open Workspace Map
            <Map className="size-4" aria-hidden="true" />
          </a>
        </>
      }
    >
      <div className="grid gap-10">
          <section id="today-priorities" className="grid gap-5">
            <SectionHeading
              eyebrow="Today's Priority Queue"
              title="Start with the work that can block clients, revenue, or delivery."
              subtitle="These cards make the first screen operational: what matters, why it matters, and where to go next."
            />
            <div className="grid gap-4 lg:grid-cols-5">
              {priorities.map((priority) => (
                <article
                  key={priority.title}
                  className="group rounded-[1.25rem] border border-white/10 bg-slate-950/70 p-5 transition hover:-translate-y-1 hover:border-cyan-300/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] ${priority.tone}`}>
                      {priority.level}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{priority.module}</span>
                  </div>
                  <h3 className="mt-4 text-xl font-black leading-7 text-white">{priority.title}</h3>
                  <p className="mt-3 text-sm font-semibold leading-6 text-slate-300">{priority.why}</p>
                  <Link
                    to={priority.to}
                    className="focus-ring mt-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-black text-slate-950 transition group-hover:bg-cyan-200"
                  >
                    {priority.action}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </article>
              ))}
            </div>
          </section>

          <section className="grid gap-5">
            <SectionHeading
              eyebrow="Operational Engine Summary"
              title="Universal activity, reminders, notes, and AR communication signals."
              subtitle="This summary reads the shared operational persistence layer so cross-surface work appears in the Business Center OS command center."
            />
            <p className="rounded-[1.25rem] border border-white/10 bg-white/[.06] px-4 py-3 text-xs font-black uppercase tracking-[0.12em] text-slate-300">
              {operationalPersistenceStatus}
            </p>
            <Link
              to="/admin/business-notes"
              className="focus-ring inline-flex min-h-11 w-fit items-center justify-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-4 text-sm font-black text-cyan-50 transition hover:-translate-y-0.5 hover:bg-cyan-300/15"
            >
              Open Business Notes
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
            {hasOperationalRecords ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {operationalSummaryCards.map((card) => (
                  <article key={card.label} className="rounded-[1.25rem] border border-white/10 bg-slate-950/70 p-5">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-cyan-100">{card.label}</p>
                    <p className="mt-2 text-3xl font-black text-white">{card.value}</p>
                    <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">{card.note}</p>
                  </article>
                ))}
              </div>
            ) : (
              <div className="rounded-[1.25rem] border border-dashed border-cyan-300/30 bg-cyan-300/10 p-5 text-sm font-semibold leading-7 text-cyan-50">
                Operational activity will appear here as teams add notes, create reminders, prepare letters, and complete workflow actions.
              </div>
            )}
          </section>

          <section className="grid gap-5">
            <SectionHeading
              eyebrow="Intelligence Snapshot"
              title="Risk, momentum, recommendations, and capacity in one review surface."
              subtitle="The existing Business Center intelligence payload is reused here so the home page acts on the platform data model instead of becoming a disconnected dashboard."
            />
            <div className="rounded-[1.5rem] border border-cyan-300/20 bg-cyan-300/10 p-4 text-sm font-semibold leading-7 text-cyan-50">
              AI-style recommendations are operational guidance only and should be reviewed by a human before
              client-facing action.
            </div>
            <IntelligenceWidgetGrid payload={intelligence} mode="admin" />
          </section>

          <section className="grid gap-5">
            <SectionHeading
              eyebrow="Client Journey Overview"
              title="Track the full path from inquiry to retention."
              subtitle="Business Center OS connects records across the complete client journey instead of leaving each stage isolated."
            />
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {journeyCounts.map((stage) => (
                <Link
                  key={stage.label}
                  to={stage.to}
                  className="focus-ring rounded-[1.25rem] border border-white/10 bg-white/[.06] p-5 transition hover:-translate-y-1 hover:border-cyan-300/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-black text-white">{stage.label}</h3>
                    <span className="rounded-full bg-slate-950 px-3 py-1 text-xs font-black text-cyan-100">{stage.count}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-300">{stage.status}</p>
                  <p className="mt-4 text-sm font-black text-cyan-100">{stage.action}</p>
                </Link>
              ))}
            </div>
          </section>

          <section id="workspace-map" className="grid gap-5">
            <SectionHeading
              eyebrow="Workspace Map"
              title="An organized operating map for the full ecosystem."
              subtitle="Module access remains intact, but each surface now sits inside a clear operating category with purpose, status, and use case."
            />
            <div className="grid gap-6">
              {moduleGroups.map((group) => (
                <section key={group.title} className="rounded-[1.5rem] border border-white/10 bg-slate-950/60 p-5">
                  <div className="flex flex-col gap-2 border-b border-white/10 pb-4 md:flex-row md:items-end md:justify-between">
                    <div>
                      <h3 className="text-2xl font-black text-white">{group.title}</h3>
                      <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-300">{group.description}</p>
                    </div>
                    <span className="w-fit rounded-full border border-white/10 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-slate-300">
                      {group.modules.length} surfaces
                    </span>
                  </div>
                  <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {group.modules.map((module) => (
                      <ModuleCard key={module.title} module={module} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </section>

          <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
            <div className="grid gap-5">
              <SectionHeading
                eyebrow="Recent Activity Timeline"
                title="Recent movement across portals, vaults, workflows, and workspaces."
                subtitle="The timeline keeps cross-platform work visible even when the action originated inside a specific module."
              />
              <div className="rounded-[1.5rem] border border-white/10 bg-white/[.06] p-5">
                <div className="grid gap-3">
                  {recentActivity.map((item) => (
                    <article key={`${item.time}-${item.action}`} className="grid gap-3 rounded-2xl border border-white/10 bg-slate-950/70 p-4 md:grid-cols-[92px_160px_minmax(0,1fr)_140px] md:items-center">
                      <p className="text-sm font-black text-cyan-100">{item.time}</p>
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">{item.module}</p>
                      <p className="text-sm font-semibold leading-6 text-white">{item.action}</p>
                      <span className="w-fit rounded-full border border-white/10 px-3 py-1 text-xs font-black text-slate-200">{item.status}</span>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            <aside className="rounded-[1.5rem] border border-white/10 bg-slate-950/70 p-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">Marketplace / Extensions</p>
              <h2 className="mt-3 text-2xl font-black text-white">Recommended next capabilities</h2>
              <p className="mt-3 text-sm font-semibold leading-7 text-slate-300">
                Marketplace routes are not registered yet, so this widget stays non-breaking and does not invent links.
              </p>
              <div className="mt-5 grid gap-3">
                {[
                  "Industry pack: Recovery operations",
                  "Automation recipe: onboarding document chase",
                  "AI assistant: business analyst summary",
                  "Memory Media Vault storage upgrade",
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-white/10 bg-white/[.06] p-4 text-sm font-semibold text-slate-200">
                    {item}
                  </div>
                ))}
              </div>
              <span className="mt-5 inline-flex rounded-full border border-[#FF6A2B]/30 bg-[#FF6A2B]/10 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-[#FDE68A]">
                Coming Soon
              </span>
            </aside>
          </section>

          {error ? (
            <section className="rounded-[1.5rem] border border-amber-300/30 bg-amber-300/10 p-5 text-sm font-semibold leading-7 text-amber-50">
              {error}
            </section>
          ) : null}
      </div>
    </BusinessCenterOSAdminShell>
  );
}

type ModuleCardData = {
  title: string;
  description: string;
  to?: string;
  icon: LucideIcon;
  status: string;
  useCase: string;
  accent?: string;
};

function ModuleCard({ module }: { module: ModuleCardData }) {
  const Icon = module.icon;
  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className={`grid size-11 shrink-0 place-items-center rounded-2xl bg-white/8 ${module.accent || "text-cyan-100"}`}>
          <Icon className="size-5" aria-hidden="true" />
        </span>
        <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-slate-300">
          {module.status}
        </span>
      </div>
      <h4 className="mt-4 text-xl font-black text-white">{module.title}</h4>
      <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">{module.description}</p>
      <div className="mt-4 rounded-2xl border border-white/10 bg-white/[.05] p-3">
        <p className="text-xs font-black uppercase tracking-[0.14em] text-cyan-100">Operational use case</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">{module.useCase}</p>
      </div>
      <span className="mt-5 inline-flex items-center gap-2 text-sm font-black text-cyan-100">
        {module.to ? "Open surface" : "Coming soon"}
        {module.to ? <ArrowRight className="size-4" aria-hidden="true" /> : null}
      </span>
    </>
  );

  if (!module.to) {
    return <article className="rounded-[1.25rem] border border-white/10 bg-white/[.05] p-5">{content}</article>;
  }

  return (
    <Link
      to={module.to}
      className="focus-ring rounded-[1.25rem] border border-white/10 bg-white/[.05] p-5 transition hover:-translate-y-1 hover:border-cyan-300/40 hover:bg-white/[.08]"
    >
      {content}
    </Link>
  );
}

function SectionHeading({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle: string }) {
  return (
    <div>
      <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">{eyebrow}</p>
      <h2 className="mt-2 max-w-5xl text-3xl font-black leading-tight text-white sm:text-4xl">{title}</h2>
      <p className="mt-3 max-w-4xl text-sm font-semibold leading-7 text-slate-300">{subtitle}</p>
    </div>
  );
}
